/**
 * Asking a model which authoring action was meant.
 *
 * The same proxy the tool assistants use (`/api/route`, functions/index.js),
 * the same request shape, and the same trust management: an id that is not
 * one of the nine is treated as no match, every failure falls back to the
 * offline router, and the model never sees the floor plan, never names a
 * station and never writes a word the author reads.
 *
 * WHY IT IS SAFE TO REUSE THE ENDPOINT. That proxy is a classifier: it is
 * handed a list of ids with a summary and an example each, and it answers
 * with one of them or null. Nothing about it is specific to an experiment -
 * the specificity is entirely in the catalogue the client sends. So the
 * authoring assistant sends its own catalogue and gets back its own ids,
 * with no server change and nothing new to deploy. The prompt on the far
 * side talks about experiments rather than edits, which costs this a little
 * accuracy on an odd sentence and costs the classroom nothing, because the
 * offline router is a full implementation rather than a stub.
 *
 * WHAT THE MODEL IS NOT ASKED. Which tool. Which stage. What to call it.
 * Those are all read out of the request against the translated catalogue in
 * `match.ts`, on this device, and a model cannot reach them.
 */

import { ROUTER_ENDPOINT, ROUTER_TIMEOUT_MS } from '../llm/config'
import { hostedRoutingEnabled } from '../llm'
import { storedProvider } from '../llm/provider'
import { guessRegion, storedRegion } from '../llm/region'
import { AUTHOR_ACTIONS, exampleOf, routeActionLocally, summaryOf, type AuthorActionId } from './actions'

export interface ActionRouting {
  /** The action to perform, or null when nothing matched. */
  id: AuthorActionId | null
  /**
   * The closest action, when the model could not place the request. Offered
   * to the author as a question rather than performed - a floor plan is the
   * author's, and guessing at it is worse than asking.
   */
  nearest: AuthorActionId | null
  /** True when this device decided, rather than the model. */
  local: boolean
}

const valid = (id: unknown): AuthorActionId | null =>
  typeof id === 'string' && (AUTHOR_ACTIONS as readonly string[]).includes(id)
    ? (id as AuthorActionId)
    : null

/**
 * Same request, same answer, for this session. A class of thirty typing the
 * four suggested requests should not be thirty times the token spend, and
 * routing is deterministic from the author's point of view either way.
 */
const cache = new Map<string, ActionRouting>()

const localRouting = (question: string): ActionRouting => ({
  id: routeActionLocally(question),
  nearest: null,
  local: true,
})

export async function routeAuthorAction(question: string): Promise<ActionRouting> {
  if (!hostedRoutingEnabled()) return localRouting(question)

  const key = question.trim().toLowerCase()
  const hit = cache.get(key)
  if (hit) return hit

  const abort = new AbortController()
  const timer = setTimeout(() => abort.abort(), ROUTER_TIMEOUT_MS)
  try {
    const res = await fetch(ROUTER_ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      signal: abort.signal,
      body: JSON.stringify({
        // Ignored by the proxy today. It is here so a server that wants to
        // keep authoring sentences out of its `route.miss` curriculum log
        // can tell them apart without parsing the catalogue; until then
        // the nine action ids in `catalogue` are the distinguishing mark.
        kind: 'author',
        question,
        intents: AUTHOR_ACTIONS.map((id) => ({
          id,
          summary: summaryOf(id),
          example: exampleOf(id),
        })),
        provider: storedProvider(),
        override: storedRegion(),
        guess: guessRegion(),
      }),
    })
    if (!res.ok) return localRouting(question)
    const data = (await res.json()) as { id?: unknown; nearest?: unknown }
    const id = valid(data.id)
    const routing: ActionRouting = {
      id,
      nearest: id ? null : valid(data.nearest),
      local: false,
    }
    // A model that placed nothing AND named nothing close has told us less
    // than the pattern router would have. Fall back rather than shrug.
    if (!routing.id && !routing.nearest) return localRouting(question)
    cache.set(key, routing)
    return routing
  } catch {
    // Offline, timed out, or the proxy is not deployed. The floor can still
    // be built by talking to it - that is why the offline router is a
    // complete implementation and not a stub.
    return localRouting(question)
  } finally {
    clearTimeout(timer)
  }
}
